/**
 * Pure date-bucketing logic for the staff-task board. No React — unit-testable.
 */

export const DONE = 'done';
export const OPEN = 'open';
export const CANCELLED = 'cancelled';
export const IN_PROGRESS = 'in_progress';

/** Приоритеты, которые предлагает интерфейс (ТЗ 0111: «Обычный / Высокий»).
 *  `low` сервер ещё знает — такие задачи показываются как обычные. */
export const PRIORITIES = ['normal', 'high'] as const;
export const HIGH_PRIORITY = 'high';

/** Значения для серверного фильтра: «Обычный» включает и старый `low`,
 *  иначе такие задачи пропадали бы из выборки, хотя показаны обычными. */
export const priorityFilterValues = (selected: string[]): string[] =>
  selected.includes('normal') && !selected.includes('low')
    ? [...selected, 'low']
    : selected;

/** Local-time `YYYY-MM-DD`. */
const pad = (n: number) => String(n).padStart(2, '0');
const fmt = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const localToday = (): string => fmt(new Date());
export const addDays = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return fmt(new Date(y, m - 1, d + n));
};

export type Bucket =
  | 'overdue'
  | 'today'
  | 'tomorrow'
  | 'thisWeek'
  | 'later'
  | 'noDate'
  | 'done';

export interface IStaffTask {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  /** `normal` | `high` (ТЗ 0111 вернуло приоритет после 0109). */
  priority?: string | null;
  /** Планируемое начало (ТЗ 0062) — левый край полосы на диаграмме Ганта.
   *  Без него задача рисуется маркером на дате срока. */
  startDate?: string | null;
  dueDate: string | null;
  /** Manual board rank (drag-and-drop). */
  position?: number | null;
  /** Spun off from a conversation message. */
  fromMessage?: boolean | null;
  completedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  /** Assignee pk — the SAME id space as `staffUsers { id }`. Filtering and
   *  the drawer's reassign select match on this, never on the display name
   *  (two staff can share one). Null when the assignee was deleted. */
  assigneeId?: string | null;
  assigneeName?: string | null;
  createdByName?: string | null;
  /** С кем связана задача (ТЗ 0064). До 0064 это были четыре вложенных
   *  объекта и ровно один предмет; теперь их сколько угодно и разных видов. */
  links?: ITaskLink[] | null;
  /** Сколько реплик в обсуждении. Тела — только в карточке. */
  commentsCount?: number | null;
  /** Файлы задачи и её реплик (ТЗ 0066). Список и счётчик — ОДНА выборка:
   *  разделив их, получили бы число, спорящее со списком под собой. */
  attachments?: ITaskAttachment[] | null;
  attachmentsCount?: number | null;
  /** Первая картинка самой задачи — обложка карточки доски (ТЗ 0109). */
  coverAttachmentId?: string | null;
}

/** Одно вложение. Ссылки здесь НЕТ намеренно: её минтит отдельный запрос по
 *  клику — подписанная ссылка живёт пять минут, и в кеше ей не место. */
export interface ITaskAttachment {
  id: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  isImage: boolean;
  /** Непусто — файл приложен к реплике, а не к самой задаче. */
  commentId?: string | null;
  uploadedByName?: string | null;
  createdAt?: string | null;
  /** Право удалить считает СЕРВЕР той же функцией, что охраняет мутацию. */
  canDelete: boolean;
}

/** Одна связь задачи. Плоская запись, а не объект сущности: двенадцать разных
 *  типов ради строки-подписи стоили бы двенадцати фрагментов и тяжёлых типов
 *  в каждом запросе. */
export interface ITaskLink {
  id: string;
  kind: string;
  objectId: string;
  name: string;
  /** Приписка: курс у группы, дата у занятия, e-mail у человека. */
  hint?: string | null;
  /** Объекта больше нет в базе. Строку НЕ прячем — иначе задача теряет
   *  причину своего существования; показываем как «объект удалён». */
  missing?: boolean | null;
  /** ТЗ 0088: готовый адрес связи от сервера — только у вида, чей адрес
   *  знает сервер (сигнал маркетинга). `null` — у прочих видов и у того,
   *  кому раздел закрыт. */
  href?: string | null;
}

/** Связи задачи одного вида — в порядке, в котором их прислал сервер. */
export const linksOf = (task: IStaffTask, kind: string): ITaskLink[] =>
  (task.links || []).filter((link) => link.kind === kind);

/** Подпись первой связи такого вида — для мест, где помещается одна строка
 *  (карточка на доске, полоса Ганта). Остальные видны в окне задачи. */
export const firstLinkName = (task: IStaffTask, kind: string): string =>
  linksOf(task, kind)[0]?.name || '';

/** What a task is ABOUT (spec 0014) — exactly one of these, or nothing. */
export type SubjectKind = 'student' | 'group' | 'teacher' | 'none';

export const SUBJECT_KINDS: SubjectKind[] = [
  'student',
  'group',
  'teacher',
  'none',
];

/**
 * Subject payload for create/update. Mirrors the backend `TaskSubjectInput`:
 * applying it rewrites ALL the subject links at once, so switching kind can't
 * leave a stale one behind. `agreementId`/`groupId` are context for
 * kind='student' only.
 */
export interface ITaskSubject {
  kind: SubjectKind;
  id?: string;
  agreementId?: string;
  groupId?: string;
}

/** Which kind a loaded task currently is. Teacher first — it is exclusive. */
/** Какого вида предмет у задачи — для фильтра «Тип связи».
 *
 *  Порядок ветвлений сохранён с ТЗ 0014, когда предмет был ровно один. Теперь
 *  видов у одной задачи может быть несколько, и функция отвечает на вопрос
 *  «к какой полке её отнести», а не «чем она является целиком»; `none` —
 *  «связей нет вовсе». Полноценный разбор по всем видам делает фильтр по
 *  связям на сервере. */
export const subjectKindOf = (task: IStaffTask): SubjectKind => {
  const kinds = new Set((task.links || []).map((link) => link.kind));
  if (kinds.has('teacher')) return 'teacher';
  if (kinds.has('student')) return 'student';
  if (kinds.has('group')) return 'group';
  return 'none';
};

/** Payload for creating a task (inline / modal). `status` seeds the column a
 *  «+» button was clicked in (status grouping); defaults to open server-side. */
export interface ICreateTaskDraft {
  title: string;
  description?: string;
  /** Скоупленная доска (вкладка студента/группы/преподавателя) задаёт предмет
   *  старым языком — сервер понимает его как «завести связь такого вида». */
  subject?: ITaskSubject;
  /** Связи для обычного создания: все двенадцать видов сразу (ревизия 0064). */
  links?: Array<{ kind: string; id: string }>;
  assigneeId?: string;
  dueDate?: string;
  status?: string;
  /** `normal` | `high`; не задан — сервер ставит обычный. */
  priority?: string;
}

export const isClosed = (task: IStaffTask): boolean =>
  task.status === DONE || task.status === CANCELLED;

export const studentName = (task: IStaffTask): string =>
  firstLinkName(task, 'student');

export const teacherName = (task: IStaffTask): string =>
  firstLinkName(task, 'teacher');

export const groupName = (task: IStaffTask): string =>
  firstLinkName(task, 'group');

export const bucketOf = (
  task: IStaffTask,
  today: string,
  tomorrow: string,
  weekEnd: string,
): Bucket => {
  if (isClosed(task)) return 'done';
  const d = task.dueDate;
  if (!d) return 'noDate';
  if (d < today) return 'overdue';
  if (d === today) return 'today';
  if (d === tomorrow) return 'tomorrow';
  if (d <= weekEnd) return 'thisWeek';
  return 'later';
};

export type TBucketMap = Record<Bucket, IStaffTask[]>;

export const emptyBuckets = (): TBucketMap => ({
  overdue: [],
  today: [],
  tomorrow: [],
  thisWeek: [],
  later: [],
  noDate: [],
  done: [],
});

export const bucketTasks = (tasks: IStaffTask[], today: string): TBucketMap => {
  const tomorrow = addDays(today, 1);
  const weekEnd = addDays(today, 7);
  const map = emptyBuckets();
  tasks.forEach((task) => {
    map[bucketOf(task, today, tomorrow, weekEnd)].push(task);
  });
  return map;
};

/**
 * The five always-visible date columns. `noDate` tasks ride in «Позже» (a 6th
 * column would starve the others on laptop widths); done/cancelled live in a
 * collapsible strip below. `overdue` is not a drag drop-target — you cannot
 * reschedule a task into the past.
 *
 * Тона здесь больше НЕТ (ТЗ 0065): он был необязательным полем, его завели
 * двум колонкам из пяти, и три оставшиеся годами не отличались друг от друга
 * ничем, кроме подписи. Теперь цвет — функция ключа (`columnTone` в
 * `taskTones`), и забыть его нельзя.
 */
export const COLUMNS: Array<{ key: Bucket }> = [
  { key: 'overdue' },
  { key: 'today' },
  { key: 'tomorrow' },
  { key: 'thisWeek' },
  { key: 'later' },
];

/* --- Status grouping (kanban): open / in progress / done -------------------- */

export type StatusKey = 'open' | 'in_progress' | 'done';

export const STATUS_COLUMNS: Array<{ key: StatusKey }> = [
  { key: 'open' },
  { key: 'in_progress' },
  { key: 'done' },
];

export type TStatusMap = Record<StatusKey, IStaffTask[]>;

/** Bucket tasks by status column (cancelled shows alongside done). */
export const bucketByStatus = (tasks: IStaffTask[]): TStatusMap => {
  const map: TStatusMap = { open: [], in_progress: [], done: [] };
  tasks.forEach((task) => {
    if (task.status === IN_PROGRESS) map.in_progress.push(task);
    else if (isClosed(task)) map.done.push(task);
    else map.open.push(task);
  });
  return map;
};

/** Patch applied when a card is dropped into a DEADLINE column: reschedule it
 *  to a concrete date. `thisWeek`/`later` keep an already-in-range date. */
export interface IDeadlineDrop {
  targetDueDate?: string;
}

export const deadlineDropPatch = (
  bucket: Bucket,
  today: string,
  currentDue: string | null,
): IDeadlineDrop | null => {
  const tomorrow = addDays(today, 1);
  const weekEnd = addDays(today, 7);
  switch (bucket) {
    case 'today':
      return { targetDueDate: today };
    case 'tomorrow':
      return { targetDueDate: tomorrow };
    case 'thisWeek':
      return currentDue && currentDue > tomorrow && currentDue <= weekEnd
        ? {}
        : { targetDueDate: addDays(today, 2) };
    case 'later':
      return currentDue && currentDue > weekEnd
        ? {}
        : { targetDueDate: addDays(today, 8) };
    default:
      return null; // overdue / noDate / done — not a drop target
  }
};

/**
 * Начало после переноса срока (ТЗ 0109) — зеркало правила `MoveStaffTask`
 * на сервере: начало позже нового срока подтягивается к сроку.
 */
export const startAfterMove = (
  startDate: string | null | undefined,
  dueDate: string,
): string | null =>
  startDate && startDate > dueDate ? dueDate : startDate ?? null;
